import {useForm} from 'react-hook-form'
import {ConfirmationModal} from '../../../../components/ConfirmationModal'
import {BadgeDetailsContext} from '../../context'
import {useContext, useState} from 'react'
import Search from '../../../../components/Search'
import InfiniteScrollWrapper from '../../../../components/InfiniteScrollWrapper'
import NoDataFound from '../../../../components/NoDataFound'
import Loading from '../../../loading'
import { getBadgeColor } from '../../../../utils/badge'

interface ProductSelectModalProps {
  onClose: () => void
  selectedProducts?: string[]
  onProductsSelected: (products: string[]) => void
}

const ProductSelectModal = ({
  onClose,
  selectedProducts = [],
  onProductsSelected,
}: ProductSelectModalProps) => {
  const {
    products,
    isLoadingProducts: isLoading,
    onPageChangeProducts,
    onSearchProducts,
    productPagination,
  } = useContext(BadgeDetailsContext)
  const {handleSubmit} = useForm()
  const [selected, setSelected] = useState<string[]>(selectedProducts)

  const handleProductSelection = (productId: string) => {
    if (selected.includes(productId)) {
      setSelected(selected.filter((id) => id !== productId))
    } else {
      setSelected([...selected, productId])
    }
  }

  // Sort: selected products first
  const sortedProducts = [
    ...products.filter((p: any) => selected.includes(p.id)),
    ...products.filter((p: any) => !selected.includes(p.id)),
  ]

  const onSubmit = () => {
    onProductsSelected(selected)
    onClose()
  }

  return (
    <ConfirmationModal
      show={true}
      onClose={onClose}
      onAction={handleSubmit(onSubmit)}
      title='Select Products'
      actionName='Save'
      actionBtnClass='btn-primary'
      dialogClassName='modal-dialog modal-lg'
      body={
        <>
          <div className='d-flex align-items-center justify-content-between mt-5 mb-5'>
            <Search
              placeholder='Search for a product'
              onSearch={(value) => {
                onSearchProducts(value)
              }}
            />
            <div className='d-flex align-items-center gap-5'>
              {selected?.length > 0 && (
                <span
                  className='text-decoration-underline cursor-pointer text-danger mt-2'
                  onClick={() => {
                    setSelected([])
                  }}
                >
                  Clear all
                </span>
              )}
              <p className='mt-2 mb-0'>
                Total <strong>{productPagination.total_count}</strong> Products
              </p>
            </div>
          </div>
          <InfiniteScrollWrapper
            name='products'
            loadMore={onPageChangeProducts}
            data={products}
            height={300}
          >
            <div className='table-responsive position-relative'>
              <div className='table-loader-wrapper'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mt-10'>
                  <thead className='table-row-bordered text-uppercase'>
                    <tr className='fs-6 fw-semibold text-muted'>
                      <th className='min-w-50px'></th>
                      <th className='min-w-150px'>{'SKU'}</th>
                      <th className='min-w-175px'>{'Name'}</th>
                      <th className='w-120px'>{'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products && products.length > 0
                      ? sortedProducts.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <div className='form-check form-check-sm form-check-custom text-capitalize'>
                                <input
                                  type='checkbox'
                                  className='form-check-input cursor-pointer'
                                  checked={selected.includes(row.id)}
                                  onChange={() => handleProductSelection(row.id)}
                                />
                              </div>
                            </td>
                            <td>{row.sku}</td>
                            <td>{row.name}</td>
                            <td className='text-capitalize'>
                              <span
                                className={`badge ${getBadgeColor(row?.status, 'light')} badge-lg`}
                              >
                                {row?.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      : !isLoading && <NoDataFound colspan={3} />}
                  </tbody>
                </table>
                {isLoading && <Loading />}
              </div>
            </div>
          </InfiniteScrollWrapper>
        </>
      }
    />
  )
}

export default ProductSelectModal
